import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Eye, EyeOff, PlayCircle } from "lucide-react";
import { apiClient, useAuth } from "@/lib/auth";

export const VIDEO_FILTERS = [["all", "All"], ["never", "Never opened"], ["opened", "Opened (no video)"], ["watched", "Video watched"]];
const day = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
export const videoState = (p) => p.video?.state || "never";
export const byVideo = (rows, vf) => (vf === "all" ? rows : rows.filter((p) => videoState(p) === vf));
const letters = (s) => (s.match(/[A-Za-z]/g) || []).length;

/** Exactly one chip per Portal Access row: Never opened · Opened (no video) · Video watched <date>. */
export function VideoChip({ p }) {
  const v = p.video || { state: "never" };
  const [label, Icon, tone] = v.state === "watched"
    ? [v.staff ? "Video watched (staff marked)" : `Video watched ${day(v.at)}`, CheckCircle2, "border-emerald-400/50 text-emerald-300"]
    : v.state === "opened" ? ["Opened (no video)", Eye, "border-amber-400/50 text-amber-200"] : ["Never opened", EyeOff, "border-white/20 text-slate-400"];
  return (
    <span data-testid={`rd-video-chip-${p.id}`} data-state={v.state} title={v.state === "watched" && v.at ? new Date(v.at).toLocaleString() : undefined}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border font-mono text-[9px] uppercase tracking-[0.12em] whitespace-nowrap ${tone}`}>
      <Icon className="h-3 w-3" />{label}
    </span>
  );
}

export function VideoFilter({ value, setValue, counts }) {
  return (
    <label className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.14em] text-nex-muted">
      <PlayCircle className="h-3 w-3" /> Video
      <select value={value} onChange={(e) => setValue(e.target.value)} data-testid="rd-video-filter" aria-label="Filter by welcome video"
        className="[color-scheme:dark] bg-black/40 border border-white/10 rounded-sm px-2 py-1 text-[11px] normal-case tracking-normal text-nex-silver focus:outline-none focus:border-nex-gold/60">
        {VIDEO_FILTERS.map(([k, l]) => <option key={k} value={k} data-testid={`rd-video-filter-${k}`}>{l}{counts ? ` (${counts[k]})` : ""}</option>)}
      </select>
    </label>
  );
}

/** Staff fix: mark the welcome tour watched by hand, with a 5+ letter note (logged with who and when). */
export function MarkWatched({ p, onDone }) {
  const { user } = useAuth();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (user?.role === "partner_user" || videoState(p) === "watched") return null;
  const ok = letters(note) >= 5;
  const save = async () => {
    if (!ok) { toast.error("Write a note of at least 5 letters"); return; }
    setBusy(true);
    try { await apiClient.post(`/re/welcome-video/${p.id}/mark`, { note }); toast.success("Marked video watched"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); } finally { setBusy(false); }
  };
  return (
    <div className="mb-2 p-2 border border-white/10 rounded-sm space-y-1.5" data-testid={`rd-video-mark-${p.id}`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-nex-muted">Mark video watched manually</div>
      <div className="flex gap-2">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why? (at least 5 letters)" data-testid={`rd-video-mark-note-${p.id}`}
          className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded-sm px-2 py-1 text-xs text-nex-silver focus:outline-none focus:border-nex-gold/60" />
        <button type="button" onClick={save} disabled={busy} data-testid={`rd-video-mark-save-${p.id}`}
          className={`px-2.5 py-1 border rounded-sm font-mono text-[10px] uppercase tracking-[0.14em] ${ok ? "border-emerald-400/60 text-emerald-300" : "border-white/15 text-slate-500"}`}>Save</button>
      </div>
    </div>
  );
}

/** Super admin setting: when the tour changes version, should everyone re-watch? Default off. */
export function RewatchToggle() {
  const [s, setS] = useState(null);
  useEffect(() => { apiClient.get("/re/welcome-video/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s?.can_edit) return null;
  const flip = async () => {
    try { await apiClient.put("/re/welcome-video/settings", { require_rewatch: !s.require_rewatch }); setS({ ...s, require_rewatch: !s.require_rewatch }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
  };
  return (
    <label className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.14em] text-nex-muted cursor-pointer">
      <input type="checkbox" checked={!!s.require_rewatch} onChange={flip} data-testid="rd-video-rewatch" className="accent-amber-400" /> Require re-watch on new tour version
    </label>
  );
}
